import { isManager } from "@/lib/access";
import { DeptFilterChip } from "@/components/DeptFilterChip";
import React, { useRef, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO, startOfDay, isWithinInterval, addDays } from "date-fns";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ComplianceCalendarDialog from "@/components/ComplianceCalendarDialog";
import { fmtAmount, fmtDate, statusOf, STATUS_STYLES, typeLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Search, Pencil, Copy, Trash2, CalendarClock, CheckCircle2, Download, Upload, CalendarIcon, Paperclip, Archive, ListChecks, ChevronLeft, ChevronRight, User, Users } from "lucide-react";
import { toast } from "sonner";
import ObligationForm from "@/components/ObligationForm";
import ImportDialog from "@/components/ImportDialog";
import MarkPaidDialog from "@/components/MarkPaidDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DUE_PERIODS = [
  { value: "all", label: "Any due period" },
  { value: "30d", label: "Next 30 days" },
  { value: "60d", label: "Next 60 days" },
  { value: "90d", label: "Next 90 days" },
  { value: "quarterly", label: "This quarter (3 mo)" },
  { value: "yearly", label: "Next 12 months" },
  { value: "custom", label: "Custom period…" },
];

export default function Obligations({ kind = "regular" }) {
  const isCompliance = kind === "compliance";
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = isManager(user);
  const [scope, setScope] = useState(() => {
    if (searchParams.get("scope") === "team") return "team";
    if (searchParams.get("scope") === "mine") return "mine";
    return isAdmin ? "team" : "mine";
  });
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState(searchParams.get("owner") || "all");
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(searchParams.get("view") === "completed" ? "completed" : "active");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "all");
  const [entityFilter, setEntityFilter] = useState(searchParams.get("entity") || "all");
  const [duePeriod, setDuePeriod] = useState(searchParams.get("due") || "all");
  const [department, setDepartment] = useState(searchParams.get("department") || "");
  const [customFrom, setCustomFrom] = useState(new Date());
  const [customTo, setCustomTo] = useState(addDays(new Date(), 30));
  const [page, setPage] = useState(Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1));
  const [perPage, setPerPage] = useState(() => {
    const p = parseInt(searchParams.get("per_page") || "25", 10);
    return [25, 50, 100].includes(p) ? p : 25;
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [ccOpen, setCcOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, cats, ts, ents] = await Promise.all([
        api.get(`/obligations?kind=${kind}&view=${view}&scope=${scope}${department && scope === "team" ? `&department_id=${department}` : ""}`),
        api.get("/categories"),
        api.get("/obligation-types"),
        api.get("/entities"),
      ]);
      setItems(data);
      setCategories(cats.data);
      setTypes(ts.data);
      setEntities(ents.data);
      // Build owner list from data (for team scope)
      if (scope === "team") {
        const uniq = {};
        data.forEach((o) => { if (o.owner_name) uniq[o.owner_name] = o.owner_email; });
        setOwners(Object.entries(uniq).map(([name, email]) => ({ name, email })));
      } else {
        setOwners([]);
      }
    } catch {
      toast.error("Failed to load obligations");
    } finally {
      setLoading(false);
    }
  }, [view, scope, department, kind]);

  useEffect(() => { load(); }, [load]);

  // Default admins to team scope when user info loads (unless URL forced mine)
  useEffect(() => {
    if (isManager(user) && scope === "mine" && !searchParams.get("scope")) setScope("team");
    // eslint-disable-next-line
  }, [user?.role]);

  // Sync URL params when filters change
  useEffect(() => {
    const params = {};
    if (view !== "active") params.view = view;
    if (scope !== "mine") params.scope = scope;
    if (statusFilter !== "all") params.status = statusFilter;
    if (duePeriod !== "all") params.due = duePeriod;
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (typeFilter !== "all") params.type = typeFilter;
    if (entityFilter !== "all") params.entity = entityFilter;
    if (ownerFilter !== "all") params.owner = ownerFilter;
    if (department) params.department = department;
    if (q.trim()) params.q = q.trim();
    if (page > 1) params.page = String(page);
    if (perPage !== 25) params.per_page = String(perPage);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, scope, statusFilter, duePeriod, categoryFilter, typeFilter, entityFilter, ownerFilter, q, page, perPage, department]);

  // Reset to page 1 whenever any filter changes (not page itself)
  const lastFilterKey = useRef(null);
  useEffect(() => {
    // Keep a deep-linked ?page=N on first render; reset only when a filter value actually changes.
    const key = JSON.stringify([view, scope, statusFilter, duePeriod, categoryFilter, typeFilter, entityFilter, ownerFilter, q, perPage]);
    if (lastFilterKey.current !== null && lastFilterKey.current !== key) setPage(1);
    lastFilterKey.current = key;
  }, [view, scope, statusFilter, duePeriod, categoryFilter, typeFilter, entityFilter, ownerFilter, q, perPage]);

  const filtered = useMemo(() => {
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const today = startOfDay(new Date());
    const now = new Date();
    const isSameMonth = (iso) => {
      if (!iso) return false;
      try {
        const d = parseISO(iso);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      } catch { return false; }
    };

    // Compute due-period window
    let periodEnd = null;
    if (duePeriod === "30d") periodEnd = addDays(today, 30);
    else if (duePeriod === "60d") periodEnd = addDays(today, 60);
    else if (duePeriod === "90d" || duePeriod === "quarterly") periodEnd = addDays(today, 90);
    else if (duePeriod === "yearly") periodEnd = addDays(today, 365);
    const customStart = duePeriod === "custom" ? startOfDay(customFrom) : null;
    const customEnd = duePeriod === "custom" ? startOfDay(customTo) : null;

    return items.filter((o) => {
      if (q && !o.name.toLowerCase().includes(q.toLowerCase()) && !(o.account_number || "").toLowerCase().includes(q.toLowerCase())) return false;
      if (typeFilter !== "all" && o.type !== typeFilter) return false;
      if (categoryFilter !== "all" && o.category !== categoryFilter) return false;
      if (entityFilter !== "all" && (o.entity || "") !== entityFilter) return false;
      if (ownerFilter !== "all" && (o.owner_name || "") !== ownerFilter) return false;

      // Status filter
      if (statusFilter === "recently-imported") {
        if (!o.imported_at) return false;
        const t = new Date(o.imported_at).getTime();
        if (isNaN(t) || t < dayAgo) return false;
      } else if (statusFilter === "paid-this-month") {
        if (!isSameMonth(o.last_paid_date)) return false;
      } else if (statusFilter !== "all") {
        if (statusOf(o.next_due_date) !== statusFilter) return false;
      }

      // Due-period filter
      if (duePeriod !== "all") {
        let due;
        try { due = startOfDay(parseISO(o.next_due_date)); } catch { return false; }
        if (duePeriod === "custom") {
          if (!customStart || !customEnd) return true;
          if (!isWithinInterval(due, { start: customStart, end: customEnd })) return false;
        } else if (periodEnd) {
          if (!isWithinInterval(due, { start: today, end: periodEnd })) return false;
        }
      }
      return true;
    });
  }, [items, q, statusFilter, typeFilter, categoryFilter, entityFilter, ownerFilter, duePeriod, customFrom, customTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const clampedPage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (clampedPage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, clampedPage, perPage]);
  const rangeStart = filtered.length === 0 ? 0 : (clampedPage - 1) * perPage + 1;
  const rangeEnd = Math.min(clampedPage * perPage, filtered.length);

  const pageNumbers = useMemo(() => buildPageList(clampedPage, totalPages), [clampedPage, totalPages]);

  const isRecentlyImported = (o) => {
    if (!o.imported_at) return false;
    const t = new Date(o.imported_at).getTime();
    return !isNaN(t) && (Date.now() - t) < 24 * 60 * 60 * 1000;
  };

  const markPaid = async (o) => {
    if (o.is_variable_amount || o.compliance_key) {
      setMarkPaidTarget(o);
      return;
    }
    try {
      await api.post(`/obligations/${o.id}/mark-paid`, {});
      toast.success(o.amount == null ? "Marked done — next due date advanced" : "Payment recorded — next due date advanced");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/obligations/${deleteId}`);
      toast.success("Obligation deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/obligations-export.csv?kind=${kind}&scope=${scope}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `obligo-export-${scope}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{isCompliance ? "Compliance" : "Ledger"}</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="obligations-page-title">{isCompliance ? "Compliance register" : "Obligations"}</h1>
          {department && <div className="mt-2"><DeptFilterChip id={department} onClear={() => setDepartment("")} /></div>}
        </div>
        <div className="flex flex-wrap gap-2">
        {isCompliance ? (
        <Button data-testid="compliance-calendar-btn" onClick={() => setCcOpen(true)} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
          <CalendarClock className="w-4 h-4" /> Add compliance calendar
        </Button>
        ) : (
        <Button data-testid="obligation-add-btn" onClick={() => { setEditing(null); setDuplicating(null); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
          <Plus className="w-4 h-4" /> New obligation
        </Button>
        )}
        </div>
      </div>
      <ComplianceCalendarDialog open={ccOpen} onOpenChange={setCcOpen} onDone={load} />

      {/* View + scope tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit">
          <button
            data-testid="view-tab-active"
            onClick={() => setView("active")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${view === "active" ? "bg-white shadow-sm text-blue-700" : "text-slate-500 hover:text-slate-700"}`}
          >
            <ListChecks className="w-3.5 h-3.5" /> Active
          </button>
          <button
            data-testid="view-tab-completed"
            onClick={() => setView("completed")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${view === "completed" ? "bg-white shadow-sm text-blue-700" : "text-slate-500 hover:text-slate-700"}`}
          >
            <Archive className="w-3.5 h-3.5" /> Completed
          </button>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit">
            <button data-testid="obligations-scope-mine" onClick={() => setScope("mine")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "mine" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><User className="w-3.5 h-3.5" /> {isCompliance ? "My filings" : "My finances"}</button>
            <button data-testid="obligations-scope-team" onClick={() => setScope("team")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "team" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><Users className="w-3.5 h-3.5" /> {isCompliance ? "Team filings" : "Team finances"}</button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="import-csv-btn" variant="outline" size="sm" onClick={() => setImportOpen(true)} className="gap-2">
          <Upload className="w-3.5 h-3.5" /> Import CSV
        </Button>
        <Button data-testid="export-csv-btn" variant="outline" size="sm" onClick={exportCsv} className="gap-2">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-7 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="search-input" placeholder="Search by name or account number…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="due-today">Due today</SelectItem>
            <SelectItem value="upcoming">Upcoming (7d)</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="paid-this-month">Paid this month</SelectItem>
            <SelectItem value="recently-imported">Recently imported (24h)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger data-testid="filter-entity"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {entities.map((e) => <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {scope === "team" && (
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger data-testid="filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map((u) => <SelectItem key={u.email} value={u.name}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={duePeriod} onValueChange={setDuePeriod}>
          <SelectTrigger data-testid="filter-due-period"><SelectValue placeholder="Due period" /></SelectTrigger>
          <SelectContent>
            {DUE_PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {duePeriod === "custom" && (
          <div className="md:col-span-7 flex flex-wrap items-end gap-3 pt-2 border-t border-slate-200">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">From</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="filter-custom-from" type="button" variant="outline" className="justify-start mono font-normal">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {customFrom ? format(customFrom, "dd MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customFrom} onSelect={(d) => d && setCustomFrom(d)} initialFocus captionLayout="dropdown-buttons" fromYear={2000} toYear={new Date().getFullYear() + 10} defaultMonth={customFrom} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">To</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="filter-custom-to" type="button" variant="outline" className="justify-start mono font-normal">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {customTo ? format(customTo, "dd MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customTo} onSelect={(d) => d && setCustomTo(d)} initialFocus captionLayout="dropdown-buttons" fromYear={2000} toYear={new Date().getFullYear() + 10} defaultMonth={customTo} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="text-xs text-slate-500 pb-1.5">Showing obligations with a next due date between the selected dates (inclusive).</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Entity</div>
          <div className="col-span-2">Category · Type</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-2 pl-4">Due date</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm text-slate-500 mb-3">
              {view === "completed" ? "No completed obligations yet." : "No obligations match your filters."}
            </div>
            {view === "active" && (
              <Button data-testid="empty-add-btn" variant="outline" onClick={() => { setEditing(null); setDuplicating(null); setDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add your first obligation
              </Button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {paginated.map((o) => {
              const isCompleted = o.is_active === false;
              const status = isCompleted ? "completed" : statusOf(o.next_due_date);
              const statusLabel = isCompleted ? "Completed" : status.replace("-", " ");
              const isOwned = !user?.id || o.user_id === user.id;
              const canManage = isOwned || isAdmin; // Admins get full rights across the org (§spec)
              return (
                <li key={o.id} data-testid={`obligation-row-${o.id}`} className="ledger-row grid grid-cols-2 md:grid-cols-12 gap-x-3 gap-y-2 px-4 md:px-5 py-4 items-start md:items-center">
                  <div className="col-span-2 md:col-span-3 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{o.name}</span>
                      <span data-testid={`status-badge-${o.id}`} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATUS_STYLES[status]}`}>{statusLabel}</span>
                      {scope === "team" && o.owner_name && (
                        <span data-testid={`owner-badge-${o.id}`} title={o.owner_email} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">{o.owner_name}</span>
                      )}
                      {o.frequency === "one-time" && !isCompleted && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200">One-Time</span>
                      )}
                      {o.payment_mode === "auto" && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">Auto</span>
                      )}
                      {isRecentlyImported(o) && (
                        <span data-testid={`imported-badge-${o.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">Imported</span>
                      )}
                      {o.attachment_count > 0 && (
                        <span
                          data-testid={`attachment-badge-${o.id}`}
                          title={`${o.attachment_count} file${o.attachment_count > 1 ? "s" : ""} attached`}
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1"
                        >
                          <Paperclip className="w-3 h-3" />
                          {o.attachment_count} file{o.attachment_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 mono">
                      {o.account_number ? `Ref ${o.account_number}` : "No account ref"}
                      {o.associated_email ? ` · ${o.associated_email}` : ""}
                    </div>
                  </div>
                  <div className="col-span-1 md:col-span-2 text-xs min-w-0" data-testid={`obligation-entity-${o.id}`}>
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Entity</div>
                    {o.entity ? (
                      <span className="text-slate-700 truncate block">{o.entity}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                  <div className="col-span-1 md:col-span-2 text-xs min-w-0">
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Category · Type</div>
                    <div className="uppercase tracking-wider text-slate-500">{o.category}</div>
                    <div className="text-slate-500">{typeLabel(o.type)}</div>
                    <div className="text-slate-400 capitalize">{o.frequency}{o.frequency === "custom" && o.custom_days ? ` · ${o.custom_days}d` : ""}</div>
                  </div>
                  <div className="col-span-1 md:text-right mono text-sm font-medium" data-testid={`obligation-amount-${o.id}`}>
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-400 mb-0.5 font-sans font-normal">Amount</div>
                    {o.amount != null ? fmtAmount(o.amount, o.currency) : <span className="text-slate-400">—</span>}</div>
                  <div className="col-span-1 md:col-span-2 md:pl-4 mono text-sm" data-testid={`obligation-due-${o.id}`}>
                    <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-400 mb-0.5 font-sans">Due date</div>
                    {isCompleted ? (
                      <span className="text-emerald-700">Completed</span>
                    ) : (
                      fmtDate(o.next_due_date)
                    )}
                    {o.last_paid_date && <div className="text-[11px] text-emerald-600">Last paid {fmtDate(o.last_paid_date)}</div>}
                  </div>
                  <div className="col-span-2 flex items-center justify-start md:justify-end gap-1 border-t border-slate-100 pt-2 md:border-0 md:pt-0">
                    {canManage ? (
                      <>
                        {!isCompleted && (
                          <Button data-testid={`mark-paid-btn-${o.id}`} size="sm" variant="outline" className="gap-1" onClick={() => markPaid(o)}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> {o.amount == null && !o.is_variable_amount ? "Done" : "Paid"}
                          </Button>
                        )}
                        <Button data-testid={`edit-btn-${o.id}`} size="icon" variant="ghost" onClick={() => { setDuplicating(null); setEditing(o); setDialogOpen(true); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`duplicate-btn-${o.id}`} size="icon" variant="ghost" title="Duplicate" onClick={() => { setEditing(null); setDuplicating(o); setDialogOpen(true); }}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`delete-btn-${o.id}`} size="icon" variant="ghost" onClick={() => setDeleteId(o.id)}>
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider" data-testid={`readonly-${o.id}`}>view only</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && filtered.length > 0 && (
          <div data-testid="pagination-bar" className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/40">
            <div className="text-xs text-slate-600 mono">
              Showing <b>{rangeStart}</b>–<b>{rangeEnd}</b> of <b>{filtered.length}</b>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>Rows</span>
                <Select value={String(perPage)} onValueChange={(v) => setPerPage(parseInt(v, 10))}>
                  <SelectTrigger data-testid="per-page-select" className="h-8 w-[72px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  data-testid="page-prev"
                  variant="outline"
                  size="sm"
                  className="h-8 px-2"
                  disabled={clampedPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {pageNumbers.map((n, i) =>
                  n === "…" ? (
                    <span key={`e-${i}`} className="px-2 text-xs text-slate-400 select-none">…</span>
                  ) : (
                    <Button
                      key={n}
                      data-testid={`page-btn-${n}`}
                      variant={n === clampedPage ? "default" : "outline"}
                      size="sm"
                      className={`h-8 w-8 p-0 mono text-xs ${n === clampedPage ? "bg-blue-700 hover:bg-blue-800 text-white" : ""}`}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </Button>
                  )
                )}
                <Button
                  data-testid="page-next"
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 gap-1"
                  disabled={clampedPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ObligationForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        prefill={duplicating}
        onSaved={() => { setDialogOpen(false); load(); }}
      />

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => { setImportOpen(false); load(); }}
      />

      <MarkPaidDialog
        open={!!markPaidTarget}
        onOpenChange={(v) => !v && setMarkPaidTarget(null)}
        obligation={markPaidTarget}
        onDone={() => { setMarkPaidTarget(null); load(); }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete obligation?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the obligation and its payment record. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Return an array of page numbers with "…" placeholders.
// Example: current=6, total=10 → [1, "…", 5, 6, 7, "…", 10]
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => pages.add(n));
  if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((n) => pages.add(n));
  const sorted = Array.from(pages).filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}
